'use client';

import { gtmPush } from '@/utils/tracking/gtm';
import { toSnakeCase } from '@/utils/text/text';
import { GTM } from '@/utils/tracking/gtm/constants';
import { SESSION_TYPE, useAccountUm } from '@/hooks/use-account-um';
import type { RootState } from '@/store/store';
import { useStore } from '@/store/store';
import { POST_LOGIN_ACTION_TYPE, setPostLoginAction } from '@/utils/post-login-action/post-login-action';
import { POPUPS } from '@/app/components/popup/constants';
import { usePathname } from 'next/navigation';
import { useGlobalContext } from '@/store/contexts/global-context';
import { COOKIES } from '@/utils/cookies';
import { getCookieValue } from '@/utils/cookies/client';
import { useMemo, useRef } from 'react';
import { useDynamicOrderingContext } from '@/app/components/zones/contexts/dynamic-ordering-context';
import { getGameSelectedProps, getTrackingData } from '@/tracking/amplitude/events/general/game-selected';
import { useCategoryTracking } from '@/tracking/snowplow/contexts/category-tracking-context';
import { EVENT_TYPE, getGameElementData, getSnowplowEvent, SNOWPLOW_CONTEXT_MAPPING } from '@/tracking/snowplow/utils';
import { sendGameElementSnowplowEvent } from '@/tracking/snowplow/send-game-element-snowplow-event';
import { classes } from '@/utils/classes/classes';
import { ensureCoreEventsInitialized } from '@/hooks/use-core-events';
import { getGameLaunchNavigationTarget } from '@/utils/url/game-launch-navigation-target';
import { getGameLaunchMode } from '@/utils/url/game-launch-mode';
import { appendEmbeddedQueryParam } from '@/logic/game-shell/shell-bridge';
import { runGameLaunchWithLoginGeolocationGate } from '@/logic/geolocation/login-geolocation-gate';
import { getLauncherUrl, type LauncherUrlProps } from '@/utils/url/launcher-url';
import { useLauncherUrlResolution } from '@/hooks/use-launcher-url-resolution';
import { useMobileWebGameLaunchGate } from '@/hooks/use-mobile-web-game-launch-gate';
import { getGameZoneInteractionProps } from '@/utils/tracking/game-zone-interaction';
import { useMlRecommendationContext } from '@/app/components/zones/contexts/ml-recommendation-context';
import { getMlRecommendationContexts } from '@/tracking/snowplow/ml-recommendation';
import type { MlRecommendationTracking } from '@/types/tracking/ml-recommendation';

type GameUrlProps = {
    gameUid?: string;
    gameId?: string;
    gameName?: string;
    module?: string;
    position?: number;
    launcherUrlProps: LauncherUrlProps;
    onLauncherClick?: () => void;
    children: React.ReactElement | React.ReactElement[];
    amplitudeEventProps?: Record<string, unknown> | (() => Record<string, unknown>);
    triggerTrackEvent?: (gameId?: string | undefined) => void;
    className?: string;
    trackingModule?: string;
};

export const GameUrl = ({
    launcherUrlProps,
    children,
    gameName,
    gameUid,
    onLauncherClick,
    amplitudeEventProps,
    triggerTrackEvent,
    className,
    trackingModule
}: GameUrlProps) => {
    const { sendEvent } = useStore((state) => state.tracking);
    const addGameWindow = useStore((state) => state.addGameWindow);
    const openEmbeddedGame = useStore((state) => state.embeddedGame.open);
    const { activePopup } = useStore((state) => state.popup);
    const launcherUrlResolution = useLauncherUrlResolution();
    const { triggerLogin } = useAccountUm();
    const { isMobile, isLoggedIn, isDesktop, isAndroidWrapper, isIosWrapper, isXsellWrapper, isPokerDesktopClient } =
        useGlobalContext();
    const { isGameLaunchBlockedOnMobileWeb, redirectToDownloadApp } = useMobileWebGameLaunchGate();
    const pathname = usePathname();
    const categoryTracking = useCategoryTracking();
    const mlRecommendationContext = useMlRecommendationContext();

    const { categoryUid } = categoryTracking;
    // Only extend the query-string launcher URL; the compact `<host>/<gameUid>` form
    // has no query string so appending would malform it.
    const launcherUrl = useMemo(() => {
        const baseLauncherUrl = getLauncherUrl(launcherUrlProps, launcherUrlResolution);
        const launcherUrlWithCategory =
            baseLauncherUrl && categoryUid && baseLauncherUrl.includes('?')
                ? `${baseLauncherUrl}&categoryUid=${encodeURIComponent(categoryUid)}`
                : baseLauncherUrl;
        return launcherUrlWithCategory ?? '';
    }, [launcherUrlProps, launcherUrlResolution, categoryUid]);

    const popup = useStore((s) => s.popup);
    const trackLaunchedGame = useStore((state: RootState) => state.dynamicOrdering.trackLaunchedGame);

    const { zoneUid: contextZoneUid, isDynamicOrderingEnabled: contextIsDynamicOrderingEnabled } =
        useDynamicOrderingContext();

    const popupParams = activePopup?.params as
        | {
              zoneUid?: string;
              isDynamicOrderingEnabled?: boolean;
              uid?: string;
              originZoneUid?: string;
              originZoneName?: string;
              originZoneType?: string;
              originZoneModule?: string;
              originZoneLayout?: string;
              originZonePosition?: string;
              originGamePosition?: string;
              originMlRecommendation?: MlRecommendationTracking | null;
          }
        | undefined;
    const zoneUid = popupParams?.zoneUid ?? contextZoneUid;
    const isDynamicOrderingEnabled = popupParams?.isDynamicOrderingEnabled ?? contextIsDynamicOrderingEnabled;
    const effectiveGameUid = popupParams?.uid ?? gameUid;
    const mlRecommendation = mlRecommendationContext ?? popupParams?.originMlRecommendation ?? null;

    const target = getGameLaunchNavigationTarget(launcherUrl, isMobile);
    const launchMode = getGameLaunchMode(launcherUrl, {
        ...launcherUrlResolution,
        isMobile,
        isAndroidWrapper,
        isIosWrapper,
        isDesktop
    });
    const gameAnchorRef = useRef<HTMLAnchorElement>(null);

    const clearPressed = () => {
        gameAnchorRef.current?.blur();
    };

    const launchEmbeddedGame = (url: string) => {
        openEmbeddedGame(appendEmbeddedQueryParam(url));
    };

    const launchGameWindow = () => {
        if (!launcherUrl) {
            return;
        }

        const region = getCookieValue(COOKIES.REGION);
        const url = launcherUrl.replace(/&region=[^&]*/, `&region=${encodeURIComponent(region ?? '')}`);

        if (launchMode === 'embed') {
            launchEmbeddedGame(url);
            return;
        }

        const gameWindow = window.open(url, target, 'width=1400,height=900');
        if (gameWindow) {
            addGameWindow(gameWindow);
        }
    };

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        const launch = () => {
            launchGameWindow();
            onLauncherClick?.();
        };

        e.stopPropagation();

        if (e.detail !== 0) {
            clearPressed();
        }

        // The desktop client wrapper handles the htcmd: navigation itself; the lobby
        // must not run any of the popup / login / window.open flows in that case.
        if (isPokerDesktopClient) {
            return;
        }

        if (typeof triggerTrackEvent === 'function') {
            triggerTrackEvent(gameUid);
        }

        const trackingData = getTrackingData(gameAnchorRef as React.RefObject<HTMLElement>);
        const snowplowGameUid = gameUid || trackingData?.gameUid || launcherUrlProps.gameUid;
        const snowplowGameName = gameName || trackingData?.gameName || launcherUrlProps.gameName;
        const isGameInfoModalPopup = activePopup?.type === POPUPS.GAME_INFO;
        const mergedTrackingData =
            trackingData && isGameInfoModalPopup
                ? {
                      ...trackingData,
                      zoneUid: popupParams?.originZoneUid ?? trackingData.zoneUid,
                      zoneName: popupParams?.originZoneName ?? trackingData.zoneName,
                      zoneType: popupParams?.originZoneType ?? trackingData.zoneType,
                      module: trackingModule ?? popupParams?.originZoneModule ?? trackingData.module,
                      zoneLayout: popupParams?.originZoneLayout ?? trackingData.zoneLayout,
                      zonePosition: popupParams?.originZonePosition ?? trackingData.zonePosition,
                      gamePosition: popupParams?.originGamePosition ?? trackingData.gamePosition
                  }
                : trackingData;

        // Title actually on screen for this zone (rotating zone titles), read at click time.
        const displayedZoneTitle = mergedTrackingData?.zoneUid
            ? useStore.getState().rotatingZoneTitle.displayedTitleByZone[mergedTrackingData.zoneUid]
            : undefined;

        const hasGameZoneTrackingContext = Boolean(
            mergedTrackingData && (mergedTrackingData.zoneName || mergedTrackingData.module)
        );

        if (amplitudeEventProps) {
            const resolvedAmplitudeEventProps =
                typeof amplitudeEventProps === 'function' ? amplitudeEventProps() : amplitudeEventProps;
            sendEvent([{ destination: 'amplitude', eventProps: resolvedAmplitudeEventProps }]);
        } else if (mergedTrackingData && hasGameZoneTrackingContext) {
            const eventProps = getGameSelectedProps({ ...mergedTrackingData, displayedZoneTitle });
            sendEvent([{ destination: 'amplitude', eventProps }]);
        }

        if (categoryTracking.categoryName) {
            void sendGameElementSnowplowEvent({
                sendEvent,
                eventType: EVENT_TYPE.CATEGORY_PAGE_GAME_SELECTED,
                eventProps: {
                    action: 'click',
                    zoneType: 'category_grid',
                    zoneName: categoryTracking.categoryName,
                    pageName: categoryTracking.categoryName,
                    zoneId: categoryTracking.zoneUid || null
                },
                trackingData,
                gameUid: snowplowGameUid ?? null,
                gameName: snowplowGameName ?? null,
                additionalContexts: getMlRecommendationContexts(mlRecommendation)
            });
        } else if (mergedTrackingData && hasGameZoneTrackingContext) {
            const pos1Based = (Number(mergedTrackingData?.gamePosition) || 0) + 1;
            const zoneInteractionProps = getGameZoneInteractionProps({
                action: 'click',
                element: e.currentTarget,
                pathname: pathname ?? '',
                fallbackZoneId: mergedTrackingData.zoneUid || mergedTrackingData.analyticsBannerId || null,
                fallbackZoneLayout: mergedTrackingData.zoneLayout ?? null,
                fallbackZoneName:
                    mergedTrackingData.bannerName || mergedTrackingData.zoneName || mergedTrackingData.module,
                fallbackZonePosition: mergedTrackingData.zonePosition,
                fallbackZoneType: mergedTrackingData.zoneType,
                ctaLabel: null
            });
            // Rotating zone titles: report the on-screen title in zone_name when present.
            const resolvedZoneName = displayedZoneTitle || zoneInteractionProps.zoneName;

            void (async () => {
                try {
                    await ensureCoreEventsInitialized();
                    const gameElement = getGameElementData({
                        position: pos1Based,
                        gameId: snowplowGameUid,
                        gameName: snowplowGameName,
                        onScreen: true,
                        gameTileType: mergedTrackingData.gameTileType ?? 'static'
                    });
                    const snowplowPayload = getSnowplowEvent(
                        EVENT_TYPE.GAME_ZONE_SELECTED,
                        { ...zoneInteractionProps, zoneName: resolvedZoneName },
                        [
                            {
                                contextType: SNOWPLOW_CONTEXT_MAPPING.GAME_IMPRESSION,
                                contextProps: gameElement
                            },
                            ...getMlRecommendationContexts(mlRecommendation)
                        ]
                    );
                    sendEvent([{ destination: 'snowplow', eventProps: snowplowPayload }]);
                } catch {
                    // Logged in ensureCoreEventsInitialized; Amplitude already sent above.
                }
            })();
        }

        if (zoneUid && isDynamicOrderingEnabled && effectiveGameUid) {
            trackLaunchedGame(zoneUid, effectiveGameUid);
        }

        gtmPush({
            event: GTM.EVENTS.GAME_SELECTED,
            gameId: effectiveGameUid,
            gameName: gameName ? toSnakeCase(gameName) : undefined
        });

        if (isGameLaunchBlockedOnMobileWeb) {
            e.preventDefault();
            redirectToDownloadApp();
            return;
        }

        if (isXsellWrapper || isAndroidWrapper) {
            e.preventDefault();
            launch();
            return;
        }

        if (isLoggedIn) {
            e.preventDefault();
            runGameLaunchWithLoginGeolocationGate(launch);
            return;
        }

        e.preventDefault();

        const requireLoginBeforeLaunch = !isDesktop || launchMode === 'embed';

        if (requireLoginBeforeLaunch) {
            setPostLoginAction({
                type: POST_LOGIN_ACTION_TYPE.LAUNCH_GAME,
                url: launcherUrl || '',
                launchMode
            });
            triggerLogin();
            return;
        }

        if (getCookieValue(COOKIES.SHOULD_DISPLAY_REGION_SELECTOR) !== 'true') {
            launchGameWindow();
            return;
        }

        popup.setOnCloseAction(() => launchGameWindow());
        popup.queuePopup({
            type: POPUPS.STATE_SELECTOR,
            params: {
                type: SESSION_TYPE.LOGIN
            }
        });
    };

    let href = launcherUrl;

    if (isPokerDesktopClient) {
        href = `htcmd:openExternalCasinoGame?gameCodeName=${gameUid}`;
    }

    return (
        <a
            data-testid="game-url"
            data-t-module={trackingModule}
            className={classes('game__url', className)}
            ref={gameAnchorRef}
            href={href}
            target={target}
            onClick={handleClick}>
            {children}
        </a>
    );
};
